import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

import LoadingSpinner from '../components/common/LoadingSpinner';
import { billingApi } from '../services/api/billing';

type State = 'verifying' | 'success' | 'failed';

/**
 * Where Paystack returns the browser after checkout. The outcome is decided by
 * our server calling Paystack's verify API - the query string is only a hint.
 */
const BillingCallbackPage: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState<State>('verifying');
  const [message, setMessage] = useState('');
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const ran = useRef(false);

  const reference = params.get('reference') || params.get('trxref');

  useEffect(() => {
    if (ran.current) return;          // StrictMode double-invoke guard
    ran.current = true;

    if (!reference) {
      setState('failed');
      setMessage('No payment reference was returned.');
      return;
    }

    billingApi
      .verify(reference)
      .then((result) => {
        if (result.status === 'success') {
          setState('success');
          setExpiresAt(result.expires_at);
          setTimeout(() => navigate('/jobs', { replace: true }), 3500);
        } else {
          setState('failed');
          setMessage(result.message ?? 'Payment was not completed.');
        }
      })
      .catch((error) => {
        setState('failed');
        setMessage(
          error?.response?.data?.detail ??
            'We could not confirm the payment. If you were charged, it will be applied automatically.'
        );
      });
  }, [reference, navigate]);

  if (state === 'verifying') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <LoadingSpinner />
        <p className="text-gray-600">Confirming your payment...</p>
      </div>
    );
  }

  const ok = state === 'success';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-white px-4">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-lg border border-gray-100 p-10 text-center">
        {ok ? (
          <CheckCircleIcon className="w-16 h-16 text-green-600 mx-auto mb-4" />
        ) : (
          <XCircleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
        )}

        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          {ok ? "You're all set" : 'Payment not completed'}
        </h1>

        <p className="text-gray-600 mb-6">
          {ok
            ? `Your pass is active${
                expiresAt ? ` until ${new Date(expiresAt).toLocaleDateString()}` : ''
              }. Taking you to your feed.`
            : message}
        </p>

        <Link
          to={ok ? '/jobs' : '/pricing'}
          className="inline-block px-6 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:shadow-lg transition-all"
        >
          {ok ? 'Go to my feed' : 'Back to pricing'}
        </Link>
      </div>
    </div>
  );
};

export default BillingCallbackPage;
